import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
	resultFromScores,
} from "../types";

export const FLIP_SIZE = 8;

export type FlipCell = PlayerIndex | null;

export interface FlipFourState extends BaseState {
	kind: "flipfour";
	board: FlipCell[];
	lastMove: number | null;
	/** Cells that changed hands on the last move, for the animation. */
	lastFlipped: number[];
	/** Set when a player had no move and had to miss a go. */
	passed: boolean;
}

export interface FlipFourMove {
	square: number;
}

const DIRECTIONS: readonly (readonly [number, number])[] = [
	[1, 0],
	[-1, 0],
	[0, 1],
	[0, -1],
	[1, 1],
	[1, -1],
	[-1, 1],
	[-1, -1],
];

/** The discs this move would flank and turn over. Empty means it is not legal. */
export function flipsFor(board: FlipCell[], square: number, player: PlayerIndex): number[] {
	if (board[square] !== null) return [];
	const col = square % FLIP_SIZE;
	const row = Math.floor(square / FLIP_SIZE);
	const them = otherPlayer(player);
	const flipped: number[] = [];

	for (const [dc, dr] of DIRECTIONS) {
		const run: number[] = [];
		let c = col + dc;
		let r = row + dr;
		while (c >= 0 && c < FLIP_SIZE && r >= 0 && r < FLIP_SIZE) {
			const cell = board[r * FLIP_SIZE + c];
			if (cell === them) {
				run.push(r * FLIP_SIZE + c);
			} else {
				// Only a line of theirs closed by one of yours counts.
				if (cell === player) flipped.push(...run);
				break;
			}
			c += dc;
			r += dr;
		}
	}
	return flipped;
}

function countDiscs(board: FlipCell[]): [number, number] {
	let mine = 0;
	let theirs = 0;
	for (const cell of board) {
		if (cell === 0) mine++;
		else if (cell === 1) theirs++;
	}
	return [mine, theirs];
}

function hasMove(board: FlipCell[], player: PlayerIndex): boolean {
	return board.some((_, square) => flipsFor(board, square, player).length > 0);
}

export const flipFour: GameDefinition<FlipFourState, FlipFourMove> = {
	id: "flipfour",
	category: "classics",
	title: "Flip Four",
	emoji: "⚫",
	blurb: "Trap your friend's discs between yours and flip them to your colour.",
	howTo: [
		"Place a disc so it traps a line of your friend's discs between two of yours.",
		"Every disc you trap flips to your colour.",
		"If you have no move that traps anything, you miss a go.",
		"Most discs when the board fills up wins.",
	],

	create(rng) {
		const board = Array<FlipCell>(FLIP_SIZE * FLIP_SIZE).fill(null);
		// The usual four in the middle.
		const middle = FLIP_SIZE / 2;
		board[(middle - 1) * FLIP_SIZE + middle - 1] = 1;
		board[(middle - 1) * FLIP_SIZE + middle] = 0;
		board[middle * FLIP_SIZE + middle - 1] = 0;
		board[middle * FLIP_SIZE + middle] = 1;

		return {
			kind: "flipfour",
			board,
			lastMove: null,
			lastFlipped: [],
			passed: false,
			// The opener has the tempo, so it is drawn by lot rather than handed
			// to whoever happens to sit in seat 0.
			turn: rng.int(2) as PlayerIndex,
			scores: countDiscs(board),
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const square = (raw as { square?: unknown }).square;
		if (typeof square !== "number" || !Number.isInteger(square)) return null;
		if (square < 0 || square >= FLIP_SIZE * FLIP_SIZE) return null;
		return { square };
	},

	apply(state, player, move): MoveOutcome<FlipFourState> {
		if (state.result) return reject("This game is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");

		const flipped = flipsFor(state.board, move.square, player);
		if (flipped.length === 0) return reject("That doesn't trap any of their discs.");

		const board = [...state.board];
		board[move.square] = player;
		for (const cell of flipped) board[cell] = player;

		const them = otherPlayer(player);
		// If they cannot move, the turn bounces straight back.
		const theyCanMove = hasMove(board, them);
		const iCanMove = hasMove(board, player);
		const scores = countDiscs(board);

		return {
			ok: true,
			state: {
				...state,
				board,
				scores,
				lastMove: move.square,
				lastFlipped: flipped,
				passed: !theyCanMove && iCanMove,
				turn: theyCanMove ? them : iCanMove ? player : null,
				result: !theyCanMove && !iCanMove ? resultFromScores(scores) : null,
			},
		};
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		const moves: FlipFourMove[] = [];
		state.board.forEach((_, square) => {
			if (flipsFor(state.board, square, player).length > 0) moves.push({ square });
		});
		return moves;
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},
};
